// KidGenerationLockscreen — fullscreen blocking overlay shown while
// any kid-side AI image generation is in flight. Two callers today:
//
//   1. Craft.jsx COLOR THIS → kid-color-craft edge fn (~15-25s)
//   2. KidSvgColoring ✨ MAGIC → generate-coloring-source edge fn (~15s)
//
// Why a hard lockscreen: the kid taps generate, the edge fn starts
// running on the server, and previously the kid could close the
// overlay / swipe to another tab / tap the gear icon and miss the
// finished artifact entirely. The page would still land in their
// coloring book but the moment was lost. This component renders at
// position:fixed zIndex:9999 covering EVERYTHING (kids tab bar, gear
// icon, parent panel, PinPad, the calling component itself) and has
// NO dismissable element. The kid stays here until the generation
// either succeeds (caller flips `visible` off) or errors (caller
// passes `error` + `onRetry`, which gives the kid one back-out path).
//
// EMOJI-IN-UI EXCEPTION: kids-mode opt-in.

import { useEffect, useState } from "react";
import { FONT } from "../../lib/tokens";
import { playKidSfx } from "../../lib/kidSfx";

// Slow-build escalation copy. Most generations finish in ~15-25s
// but the OpenAI tail is long; show reassurance copy at the 25s
// mark so the kid (and the watching parent) don't panic.
const PATIENCE_MS = 25_000;

export default function KidGenerationLockscreen({
  visible,
  title = "Drawing your picture",
  subtitle = "Pets and crayons coming together",
  error = null,
  onRetry = null,
  onCancel = null,
}) {
  const [patient, setPatient] = useState(false);

  useEffect(() => {
    if (!visible || error) {
      setPatient(false);
      return undefined;
    }
    // Sparkle as the lockscreen mounts so the kid hears the magic
    // kick off in addition to seeing the wand.
    playKidSfx("sparkle");
    const t = setTimeout(() => setPatient(true), PATIENCE_MS);
    return () => clearTimeout(t);
  }, [visible, error]);

  if (!visible) return null;

  return (
    <div
      role="alertdialog"
      aria-live="polite"
      aria-label={title}
      // Eats every pointer event. The kid cannot tap through to
      // anything underneath — gear icon, tab bar, prior overlay X,
      // nothing. iOS still owns the home-bar swipe, but inside the
      // app this is bulletproof.
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 18,
        padding: "max(48px, env(safe-area-inset-top)) 24px max(48px, env(safe-area-inset-bottom))",
        background:
          "radial-gradient(ellipse at center, #B89BE8 0%, #7A4E2D 70%, #2F2233 100%)",
        color: "#fff",
        touchAction: "none",
        userSelect: "none",
        cursor: "default",
        overflow: "hidden",
      }}
    >
      {/* Floating sparkles around the wand. Layered absolutely so the
          centered text doesn't shift. */}
      <Sparkle top="14%"  left="18%" delay="0s"   size={28} />
      <Sparkle top="22%"  left="78%" delay="0.4s" size={20} />
      <Sparkle top="64%"  left="12%" delay="0.8s" size={24} />
      <Sparkle top="72%"  left="82%" delay="1.2s" size={30} />
      <Sparkle top="38%"  left="86%" delay="0.6s" size={18} />
      <Sparkle top="48%"  left="8%"  delay="1.0s" size={22} />

      {/* Big magic wand glyph. Wobbles continuously so the kid sees
          progress even though the server doesn't stream any. */}
      <div
        style={{
          fontSize: 120,
          lineHeight: 1,
          filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.4))",
          animation: error ? "none" : "kid-lock-wand 1.6s ease-in-out infinite",
        }}
      >
        {error ? "🥺" : "✨"}
      </div>

      <div
        style={{
          fontFamily: FONT.itemTitle,
          fontStyle: "italic",
          fontSize: 32,
          textAlign: "center",
          textShadow: "0 2px 12px rgba(0,0,0,0.4)",
          maxWidth: 340,
        }}
      >
        {error ? "Magic broke" : title}
      </div>

      <div
        style={{
          fontFamily: FONT.forma,
          fontSize: 15,
          color: "rgba(255,255,255,0.85)",
          textAlign: "center",
          maxWidth: 320,
          lineHeight: 1.45,
          minHeight: 44,
        }}
      >
        {error
          // When the caller passed real error TEXT (e.g. the weekly
          // AI-token message "it comes back Monday!"), show it —
          // hardcoding "Couldn't draw it this time." hid the honest
          // reason and invited a retry loop that could never succeed.
          ? (typeof error === "string" && error.trim() ? error : "Couldn't draw it this time.")
          : (patient ? "Almost there — just a few more seconds…" : subtitle)}
      </div>

      {/* Three-dot loader (only while waiting, not on error). */}
      {!error && (
        <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                width: 14, height: 14, borderRadius: 7,
                background: "rgba(255,255,255,0.9)",
                animation: "kid-lock-dot 1.2s ease-in-out infinite",
                animationDelay: `${i * 0.18}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Error branch: one obvious way out. Either retry or abandon
          back to the calling surface. No silent close. */}
      {error && (
        <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mcm-focusable"
              style={{
                padding: "14px 24px",
                background: "#fff",
                color: "#7A4E2D",
                border: "none",
                borderRadius: 14,
                fontFamily: FONT.mono,
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: "0.08em",
                cursor: "pointer",
                touchAction: "manipulation",
                boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
              }}
            >
              TRY AGAIN
            </button>
          )}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="mcm-focusable"
              style={{
                padding: "14px 20px",
                background: "transparent",
                color: "rgba(255,255,255,0.85)",
                border: "1.5px solid rgba(255,255,255,0.55)",
                borderRadius: 14,
                fontFamily: FONT.mono,
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: "0.08em",
                cursor: "pointer",
                touchAction: "manipulation",
              }}
            >
              GO BACK
            </button>
          )}
        </div>
      )}

      <style>{`
        @keyframes kid-lock-wand {
          0%, 100% { transform: rotate(-12deg) scale(1); }
          50%      { transform: rotate(14deg)  scale(1.12); }
        }
        @keyframes kid-lock-dot {
          0%, 80%, 100% { opacity: 0.35; transform: translateY(0); }
          40%           { opacity: 1;    transform: translateY(-6px); }
        }
        @keyframes kid-lock-sparkle {
          0%, 100% { opacity: 0.25; transform: scale(0.8) rotate(0); }
          50%      { opacity: 1;    transform: scale(1.2) rotate(180deg); }
        }
      `}</style>
    </div>
  );
}

function Sparkle({ top, left, delay, size }) {
  return (
    <span
      aria-hidden
      style={{
        position: "absolute",
        top, left,
        fontSize: size,
        animation: `kid-lock-sparkle 2.4s ease-in-out ${delay} infinite`,
        color: "#FFF6CE",
        textShadow: "0 0 12px rgba(255, 240, 180, 0.8)",
        pointerEvents: "none",
      }}
    >
      ✦
    </span>
  );
}
